import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Table, Checkbox, Message, Label, Icon, Input } from 'semantic-ui-react';
import images from '../../images';
import { formatAmount, formatCurrency, parseLocalizedAmount } from '../misc/PriceFormatter';

const formatMoney = (value) => formatCurrency(value, { stripZeroDecimals: true }).replace(/ /g, ' ');

const calculateTargetPrice = (currentPrice, priceType, increaseAmount) => {
  const normalizedCurrent = Number(currentPrice || 0);
  const normalizedAmount = Number(increaseAmount || 0);
  if (!Number.isFinite(normalizedCurrent) || !Number.isFinite(normalizedAmount)) {
    return null;
  }

  const nextPrice =
    priceType === 'percentage'
      ? normalizedCurrent + normalizedCurrent * (normalizedAmount / 100)
      : normalizedCurrent + normalizedAmount;

  if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
    return null;
  }

  return Number(nextPrice.toFixed(2));
};

const getMarketplaceIcon = (marketplace) =>
  marketplace === 'mercado_libre' ? images.meliIcon : images.tnIcon;

const stickyHeaderCellStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#f8f8f8',
};

const BulkPriceUpdateModal = ({
  open,
  onClose,
  formData,
  handleFormChange,
  handleFormSubmit,
  selectedRows,
  saving = false,
}) => {
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [previewByListingId, setPreviewByListingId] = useState({});
  const [previewReady, setPreviewReady] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [editingDraft, setEditingDraft] = useState({ listingId: null, text: '' });
  const [invalidEditIds, setInvalidEditIds] = useState([]);

  const listingRows = useMemo(
    () =>
      (selectedRows || []).flatMap((item) =>
        (item.listings || []).map((listing) => ({
          ...listing,
          parentItemId: item.id,
          parentTitle: item.title || '-',
          parentVariationName: item.variationName || '',
          canonicalSku: item.sku || '',
        })),
      ),
    [selectedRows],
  );

  const allListingIdsKey = useMemo(
    () => listingRows.map((listing) => String(listing.id || '')).filter(Boolean).join('|'),
    [listingRows],
  );
  // Keyed by content so a parent re-render with equal rows does not discard the calculated preview.
  const allListingIds = useMemo(
    () => (allListingIdsKey ? allListingIdsKey.split('|') : []),
    [allListingIdsKey],
  );

  useEffect(() => {
    if (!open) {
      setSelectedListingIds([]);
      setPreviewByListingId({});
      setPreviewReady(false);
      setValidationMessage('');
      setEditingDraft({ listingId: null, text: '' });
      setInvalidEditIds([]);
      return;
    }

    setSelectedListingIds(allListingIds);
    setPreviewByListingId({});
    setPreviewReady(false);
    setValidationMessage('');
    setEditingDraft({ listingId: null, text: '' });
    setInvalidEditIds([]);
  }, [open, allListingIds]);

  useEffect(() => {
    if (!open) return;
    setPreviewByListingId({});
    setPreviewReady(false);
    setValidationMessage('');
    setEditingDraft({ listingId: null, text: '' });
    setInvalidEditIds([]);
  }, [open, formData.priceType, formData.increaseAmount, selectedListingIds]);

  const selectedListingsCount = selectedListingIds.length;
  const allSelected = allListingIds.length > 0 && selectedListingsCount === allListingIds.length;
  const someSelected = selectedListingsCount > 0 && selectedListingsCount < allListingIds.length;

  const handleToggleAll = (_, data) => {
    setSelectedListingIds(data.checked ? allListingIds : []);
  };

  const handleToggleListing = (listingId) => {
    setSelectedListingIds((prev) =>
      prev.includes(listingId)
        ? prev.filter((id) => id !== listingId)
        : [...prev, listingId],
    );
  };

  const handleCalculate = () => {
    const normalizedAmount = Number(formData.increaseAmount);
    if (formData.increaseAmount === '' || !Number.isFinite(normalizedAmount) || normalizedAmount === 0) {
      setValidationMessage('Ingresá un valor distinto de 0. Un valor negativo aplica un descuento.');
      setPreviewReady(false);
      setPreviewByListingId({});
      return;
    }

    const nextPreview = {};
    for (const listing of listingRows) {
      const nextPrice = calculateTargetPrice(
        Number(listing.price || 0),
        formData.priceType,
        normalizedAmount,
      );
      if (nextPrice === null) {
        setValidationMessage(
          `Con ${formData.priceType === 'percentage' ? 'ese porcentaje' : 'ese monto'} el precio de "${listing.title || listing.parentTitle}" quedaría en 0 o menos. Ajustá el valor.`,
        );
        setPreviewReady(false);
        setPreviewByListingId({});
        return;
      }
      nextPreview[listing.id] = nextPrice;
    }

    setPreviewByListingId(nextPreview);
    setPreviewReady(true);
    setValidationMessage('');
  };

  const handleEditPreviewPrice = (listingId, text) => {
    setEditingDraft({ listingId, text });
    const parsed = parseLocalizedAmount(text);
    if (parsed === null) {
      setInvalidEditIds((prev) => (prev.includes(listingId) ? prev : [...prev, listingId]));
      return;
    }
    setInvalidEditIds((prev) => prev.filter((id) => id !== listingId));
    setPreviewByListingId((prev) => ({ ...prev, [listingId]: parsed }));
  };

  const handleEditPreviewBlur = (listingId) => {
    setEditingDraft({ listingId: null, text: '' });
    if (invalidEditIds.includes(listingId)) {
      // Restore the last valid preview price instead of saving an unparsable value.
      setInvalidEditIds((prev) => prev.filter((id) => id !== listingId));
    }
  };

  const hasInvalidEdits = invalidEditIds.length > 0;

  const handleSave = async () => {
    if (!previewReady || selectedListingIds.length === 0 || saving || hasInvalidEdits) {
      return;
    }

    const selectedListings = listingRows.filter((listing) => selectedListingIds.includes(listing.id));
    const updates = selectedListings.map((listing) => ({
      listingId: listing.id,
      price: previewByListingId[listing.id],
    }));

    try {
      await handleFormSubmit(updates, selectedListings);
      onClose();
    } catch (error) {
      console.error('Error updating listing prices:', error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      closeIcon
      style={{
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Modal.Header>Actualizar precios masivamente</Modal.Header>
      <Modal.Content
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Tipo de ajuste</label>
                <Form.Group inline>
                  <Form.Radio
                    label="Porcentaje (%)"
                    name="priceType"
                    value="percentage"
                    checked={formData.priceType === 'percentage'}
                    onChange={handleFormChange}
                  />
                  <Form.Radio
                    label="Monto fijo ($)"
                    name="priceType"
                    value="fixed"
                    checked={formData.priceType === 'fixed'}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Form.Field>
              <Form.Input
                type="number"
                label="Aumento (negativo = descuento)"
                step={formData.priceType === 'percentage' ? '0.01' : '1'}
                name="increaseAmount"
                value={formData.increaseAmount}
                onChange={handleFormChange}
                placeholder={formData.priceType === 'percentage' ? 'Ej: 12.5 o -10' : 'Ej: 1500 o -500'}
              />
              <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  color="purple"
                  onClick={handleCalculate}
                  disabled={listingRows.length === 0 || saving}
                >
                  Calcular
                </Button>
              </Form.Field>
            </Form.Group>
          </Form>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Label basic>
              Items seleccionados: {selectedRows.length}
            </Label>
            <Label basic>
              Publicaciones en modal: {listingRows.length}
            </Label>
            <Label color={selectedListingsCount > 0 ? 'blue' : undefined}>
              Publicaciones a actualizar: {selectedListingsCount}
            </Label>
            {previewReady ? (
              <Label color="green">Preview listo para guardar</Label>
            ) : (
              <Label basic>Calculá el preview antes de guardar</Label>
            )}
          </div>

          {validationMessage ? (
            <Message negative content={validationMessage} />
          ) : null}

          {listingRows.length === 0 ? (
            <Message info content="No hay publicaciones seleccionadas para actualizar." />
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  padding: '12px 14px',
                  background: '#f6fbff',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontWeight: 700, color: '#1f2937' }}>
                  Vas a actualizar el precio de las publicaciones seleccionadas.
                </div>
                <div style={{ marginTop: 4, color: '#4b5563' }}>
                  El nuevo precio se publicará directamente en Mercado Libre o Tienda Nube, según el canal de cada publicación.
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table celled striped compact="very" style={{ margin: 0, tableLayout: 'auto' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell collapsing textAlign="center" style={stickyHeaderCellStyle}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleToggleAll}
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ ...stickyHeaderCellStyle, width: '100%' }}>Publicacion</Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="center" style={stickyHeaderCellStyle}>Canal</Table.HeaderCell>
                    <Table.HeaderCell collapsing style={stickyHeaderCellStyle}>SKU</Table.HeaderCell>
                    <Table.HeaderCell collapsing style={stickyHeaderCellStyle}>Seller</Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="right" style={stickyHeaderCellStyle}>Actual</Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="right" style={stickyHeaderCellStyle}>Preview</Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="right" style={stickyHeaderCellStyle}>Delta</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {listingRows.map((listing) => {
                    const isSelected = selectedListingIds.includes(listing.id);
                    const previewPrice = previewByListingId[listing.id];
                    const currentPrice = Number(listing.price || 0);
                    const delta = previewPrice !== undefined ? Number((previewPrice - currentPrice).toFixed(2)) : null;

                    return (
                      <Table.Row key={listing.id} disabled={!isSelected}>
                        <Table.Cell textAlign="center">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleListing(listing.id)}
                          />
                        </Table.Cell>
                        <Table.Cell style={{ minWidth: 160, wordBreak: 'break-word' }}>
                          <div style={{ fontWeight: 600 }}>{listing.title || listing.parentTitle}</div>
                          {listing.parentVariationName ? (
                            <div style={{ color: '#777', marginTop: 4 }}>
                              {listing.parentVariationName}
                            </div>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <img
                            src={getMarketplaceIcon(listing.marketplace)}
                            alt={listing.marketplace}
                            style={{ width: 22, height: 22 }}
                          />
                        </Table.Cell>
                        <Table.Cell collapsing style={{ whiteSpace: 'nowrap' }}>{listing.sku || listing.canonicalSku || '-'}</Table.Cell>
                        <Table.Cell collapsing style={{ whiteSpace: 'nowrap' }}>{listing.sellerNickname || '-'}</Table.Cell>
                        <Table.Cell collapsing textAlign="right" style={{ whiteSpace: 'nowrap' }}>{formatMoney(currentPrice)}</Table.Cell>
                        <Table.Cell collapsing textAlign="right" style={{ whiteSpace: 'nowrap' }}>
                          {previewReady ? (
                            <Input
                              size="mini"
                              error={invalidEditIds.includes(listing.id)}
                              disabled={!isSelected}
                              style={{ width: 128 }}
                              input={{ style: { width: 0, minWidth: 0, textAlign: 'right', padding: '6px 8px' } }}
                              label={{ basic: true, content: '$' }}
                              labelPosition="left"
                              aria-label={`Precio nuevo de ${listing.title || listing.parentTitle}`}
                              value={
                                editingDraft.listingId === listing.id
                                  ? editingDraft.text
                                  : formatAmount(previewPrice)
                              }
                              onFocus={() => setEditingDraft({ listingId: listing.id, text: formatAmount(previewPrice) })}
                              onChange={(_, data) => handleEditPreviewPrice(listing.id, data.value)}
                              onBlur={() => handleEditPreviewBlur(listing.id)}
                            />
                          ) : <span style={{ color: '#999' }}>Sin calcular</span>}
                        </Table.Cell>
                        <Table.Cell collapsing textAlign="right" style={{ whiteSpace: 'nowrap' }}>
                          {previewReady ? (
                            <span style={{ color: delta >= 0 ? '#1b7f3b' : '#a33333' }}>
                              {delta >= 0 ? '+' : '-'}{formatMoney(Math.abs(delta))}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
                </Table>
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={saving}>
          Cerrar
        </Button>
        <Button
          primary
          onClick={handleSave}
          loading={saving}
          disabled={!previewReady || selectedListingIds.length === 0 || saving || hasInvalidEdits}
        >
          <Icon name="save" />
          Guardar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BulkPriceUpdateModal;
